import { useState, useEffect } from "react"
import CMSData from "aldoo-ra/CMS/cms-data"
import PubSub from "pubsub-js"

const _samples = {}
const _active = {}        // { [instanceId]: Audio }
let _soundBankId = null
const _bankIdListeners = []
const _subscriptions = []

// Unlock audio on iOS — must be called inside a user gesture handler before any async work.
export const unlock = () => {
  const a = new Audio()
  a.play().catch(() => {})
}

// Play a sample. instanceId lets you stop it later via stop(instanceId).
// If a sound with the same instanceId is already playing it is stopped first.
// Returns the instanceId so callers can stop it later.
export const play = (label, instanceId) => {
  const url = _samples[label]
  if (!url) return null

  const id = instanceId ?? `${label}_${Date.now()}`

  // Stop any previous sound bound to this instance
  stop(id)

  const audio = new Audio(url)
  _active[id] = audio
  audio.play().catch(() => {})
  audio.addEventListener("ended", () => { if (_active[id] === audio) delete _active[id] }, { once: true })

  return id
}

export const stop = (instanceId) => {
  const audio = _active[instanceId]
  if (!audio) return
  audio.pause()
  audio.currentTime = 0
  delete _active[instanceId]
}

export const registerSoundMap = (soundBankId, map) => {
  if (!soundBankId || !map) return
  _subscriptions.forEach((t) => PubSub.unsubscribe(t))
  _subscriptions.length = 0

  for (const [event, handler] of Object.entries(map)) {
    _subscriptions.push(
      PubSub.subscribe(event, (_, data) => {
        if (typeof handler === "function") handler(data)
        else play(handler, data?.instanceId)
      })
    )
  }

  _soundBankId = soundBankId
  _bankIdListeners.forEach((cb) => cb(soundBankId))
}

function BankLoader({ soundBankId }) {
  const { data } = CMSData({
    contentType: "SoundBank",
    match: { "content.id": soundBankId },
  })

  useEffect(() => {
    if (!data?.length) return
    const samples = data[0].samples
    if (!samples?.length) return
    for (const s of samples) {
      if (s.id && s.sample) _samples[s.id] = s.sample?.url || s.sample
    }
  }, [data])

  return null
}

export function SoundBankLoader() {
  const [bankId, setBankId] = useState(_soundBankId)

  useEffect(() => {
    if (_soundBankId) setBankId(_soundBankId)
    const cb = (id) => setBankId(id)
    _bankIdListeners.push(cb)
    return () => {
      const i = _bankIdListeners.indexOf(cb)
      if (i !== -1) _bankIdListeners.splice(i, 1)
    }
  }, [])

  if (!bankId) return null
  return <BankLoader soundBankId={bankId} />
}

export default { play, stop, unlock, registerSoundMap }
